// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import BackToTopButton from '../BackToTopButton/BackToTopButton';
import ExpandedGroups from '../ExpandedGroups/ExpandedGroups';
import FallbackComponent from '../ErrorBoundary/FallbackComponent';
import {HitCarouselIconDefinitions} from '../HitCarouselContainer/HitCarouselContainer';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import HitCollection, {
    fragments as hitCollectionFragments
} from '../HitCollection/HitCollection';
import Pager from '../Pager/Pager';
import MetaBar from '../MetaBar/MetaBar';
import DisclaimerMessaging from '../DisclaimerMessaging/DisclaimerMessaging';
import ResultsLoading from '../ResultsLoading/ResultsLoading';
import type {Shape as PagerShape} from '../Pager/Pager';
import displayActions from '../../actions/display';
import mapActions from '../../actions/map';
import NoResults from '../NoResults/NoResults';
import type {Shape as ResultsCountShape} from '../ResultsCount/ResultsCount';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import {scrollToAnimated} from '../../utils/scrollTo';
import {media} from '../../utils/breakpoints';
import {checkedFilters} from '../../../server/utils/filters';
import searchParamsActions from '../../actions/searchParams';
import {isEqual} from 'lodash';
import Experiment from '@homeaway/react-experiment';
import {HA_SERP_DOCK_PAGER_PANEL} from '../../abacus/experiments';
import {StickyAsFixed} from '../Sticky/Sticky';

import type {Breakpoint} from '../../utils/breakpoints';
import type {State as globalState} from '../../reducers';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {Action as MobileScrollerAction} from '../../actions/display';

import './Results.less';

type StateProps = {
    breakpoint: Breakpoint,
    abacusExperiments: Object,
    numFilters?: number
};

type Props = {
    loading: boolean,
    results: ?Object,
    allowUpdate: boolean,
    showMobileScroller: () => MobileScrollerAction,
    hideMobileScroller: () => MobileScrollerAction,
    syncInputParams: (?SearchParamsType) => any,
    highlightPin: (string) => any,
    clearHighlightedPins: () => any
} & StateProps;

type ScrolledState = {
    '0': number,
    '5': number,
    '10': number
};

type State = {
    scrolled: ScrolledState,
    currentState?: number,
    hasEvaluated: boolean,
    loggedFirstHitHover: boolean
};

const defaultScrolledState = {
    '0': -1,
    '5': -1,
    '10': -1
};

export class Results extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            loggedFirstHitHover: false,
            scrolled: defaultScrolledState,
            hasEvaluated: false
        };
    }

    componentDidMount() {
        const {loading, results} = this.props;
        if (!loading && results && results.listings) {
            // this is needed for server side rendering because when the component is mounted, the results are already available and hence
            // componentWillReceiveProps is not called
            const checked = checkedFilters(results.filterGroups);
            this.props.syncInputParams({
                filter: checked
            });
        }
    }

    // fired for client side searches
    componentWillReceiveProps(nextProps: Props) {
        if (!nextProps.loading) {
            this.updateSelectedFilters(nextProps);
        } else {
            this.props.hideMobileScroller();
        }
    }

    shouldComponentUpdate(nextProps: Props) {
        return nextProps.allowUpdate;
    }

    updateSelectedFilters(nextProps: Props) {
        const nextFilterGroups = nextProps.results
            ? nextProps.results.filterGroups
            : null;
        const filterGroups = this.props.results
            ? this.props.results.filterGroups
            : null;

        if (nextFilterGroups && !isEqual(filterGroups, nextFilterGroups)) {
            this.props.syncInputParams({
                filter: checkedFilters(nextFilterGroups)
            });
        }
    }

    handleScrollHitEnter = (label: number) => {
        const hitEntered = this.state.scrolled[label];
        const currentScrollTop =
            (window && window.scrollY) ||
            (document &&
                document.documentElement &&
                document.documentElement.scrollTop) ||
            0;
        if (hitEntered < 0) {
            edapTrigger(GENERIC_EVENT, {
                eventaction: 'page scroll',
                eventlabel: label,
                highfrequency: 'true'
            });
            this.setState({
                scrolled: {
                    ...this.state.scrolled,
                    [label]: currentScrollTop
                }
            });
        }
        this.checkForMobileScroller(currentScrollTop);
    };

    onHitMouseEnter = (listingId: string) => {
        this.handleFirstHitHover();
        this.props.highlightPin(listingId);
    };

    onHitMouseLeave = () => {
        this.props.clearHighlightedPins();
    };

    handleFirstHitHover = () => {
        const {loggedFirstHitHover} = this.state;

        if (!loggedFirstHitHover) {
            edapTrigger(GENERIC_EVENT, {
                eventaction: 'first hit hover',
                highfrequency: 'true'
            });
            this.setState({
                loggedFirstHitHover: true
            });
        }
    };

    checkForMobileScroller = (currentScrollTop: number) => {
        if (
            this.state.scrolled['10'] >= 0 &&
            currentScrollTop >= this.state.scrolled['10']
        ) {
            this.props.showMobileScroller();
        } else if (currentScrollTop < this.state.scrolled['10']) {
            this.props.hideMobileScroller();
        }
    };

    scrollToTopAnimated = () => {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'back to top click'
        });
        scrollToAnimated(0, 3000, 'easeOutSine', () => {
            this.props.hideMobileScroller();
            this.setState({scrolled: defaultScrolledState});
        });
    };

    render() {
        const {loading, results, numFilters} = this.props;

        if (loading) {
            // Loading Hit Placeholders
            return <ResultsLoading numLoadingPlaceHolders={4} />;
        } else if (results) {
            const {
                fromRecord,
                toRecord,
                resultCount,
                page,
                pageCount,
                pageSize
            } = results;
            const pagerData: PagerShape = {
                fromRecord,
                toRecord,
                resultCount,
                page,
                pageCount
            };
            const resultsCount: ResultsCountShape = {
                fromRecord,
                toRecord,
                resultCount
            };
            const transformedResults = filterGraphQlAnywhere(
                hitCollectionFragments.results,
                results
            );
            const isNoResults = !resultCount || !transformedResults.listings;
            return (
                <div
                    className="Results"
                    role="region"
                    aria-labelledby="resultsCountDisclaimerHeading"
                >
                    <HitCarouselIconDefinitions />
                    <MetaBar resultsCount={resultsCount} />

                    {(isNoResults && <NoResults numFilters={numFilters} />) || (
                        <HitCollection
                            results={transformedResults}
                            onScrollHitEnter={this.handleScrollHitEnter}
                            onHitMouseEnter={this.onHitMouseEnter}
                            onHitMouseLeave={this.onHitMouseLeave}
                        />
                    )}
                    {!media(this.props.breakpoint, {minWidth: 'SM_MIN'}) && (
                        <BackToTopButton
                            clickHandler={this.scrollToTopAnimated}
                        />
                    )}
                    {!!results.expandedGroups &&
                        !!results.expandedGroups.length && (
                            <ExpandedGroups
                                onHitMouseEnter={this.onHitMouseEnter}
                                onHitMouseLeave={this.onHitMouseLeave}
                                expandedGroups={results.expandedGroups}
                                page={page}
                                pageSize={pageSize}
                            />
                        )}

                    <Experiment
                        name={HA_SERP_DOCK_PAGER_PANEL}
                        variant={Experiment.OFF}
                    >
                        <Pager pager={pagerData} />
                    </Experiment>
                    <Experiment
                        name={HA_SERP_DOCK_PAGER_PANEL}
                        variant={Experiment.ON}
                    >
                        <StickyAsFixed
                            className="Results__stickyPagerContainer"
                            top={0}
                        >
                            <Pager pager={pagerData} />
                        </StickyAsFixed>
                    </Experiment>
                    {!isNoResults && <DisclaimerMessaging ariaHidden />}
                </div>
            );
        }
        return <FallbackComponent />;
    }
}

const mapStateToProps = (state: globalState): StateProps => {
    let {viewport, abacus} = state;
    return {
        numFilters: state.searchParams.numFilters,
        breakpoint: viewport.breakpoint,
        abacusExperiments: abacus
    };
};

const mapDispatchToProps = {
    showMobileScroller: () => displayActions.toggleMobileScroller(true),
    hideMobileScroller: () => displayActions.toggleMobileScroller(false),
    syncInputParams: searchParamsActions.syncInputParams,
    highlightPin: mapActions.highlightPin,
    clearHighlightedPins: mapActions.clearHighlightedPins
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(Results);
